/** XeAgents.ai**/
import { useEffect } from "react";

/**
 * Fallback for the fixed header height, used only before it has rendered.
 * scrollIntoView aligns an element with the TOP of the viewport, which on this
 * page means the header covers whatever you jumped to, so every jump subtracts
 * the header instead.
 */
const HEADER_OFFSET_FALLBACK = 80;

/**
 * Measured, not hardcoded: the header is the announcement bar plus the navbar,
 * and it is shorter on small screens. A constant would be wrong on most of
 * them.
 */
function headerOffset(): number {
  const header = document.querySelector("[data-site-header]");
  const measured = header?.getBoundingClientRect().height ?? 0;
  return measured > 0 ? measured : HEADER_OFFSET_FALLBACK;
}

/** A little air between the navbar and the section heading. */
const BREATHING_ROOM = 16;

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Scrolls to the section a "#hash" names, clearing the fixed navbar.
 * Returns false when no such section is on the page, so callers can decide
 * whether to keep waiting for one to render.
 */
export function scrollToSection(hash: string): boolean {
  if (typeof window === "undefined") return false;

  const id = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!id) return false;

  // The id comes from our own nav lists, but CSS.escape keeps a stray
  // character from turning a missing section into a thrown selector error.
  const selector = `#${window.CSS?.escape ? window.CSS.escape(id) : id}`;
  const element = document.querySelector(selector);
  if (!element) return false;

  const top = element.getBoundingClientRect().top + window.scrollY - headerOffset() - BREATHING_ROOM;

  window.scrollTo({
    top: Math.max(0, top),
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });

  return true;
}

/**
 * Makes "/#pricing" work as a destination, not just as an in-page jump.
 *
 * Two cases the browser will not handle for us. Arriving from another page goes
 * through wouter's setLocation, which is a pushState - no native anchor jump.
 * And on a cold load the anchor resolves before React has rendered anything, so
 * there is nothing to scroll to yet. This waits for the section to appear, then
 * corrects itself once the images above it have finished loading and stopped
 * pushing it down the page.
 */
export function useSectionHashScroll(): void {
  useEffect(() => {
    let cancelled = false;
    let frame = 0;
    let settle: ReturnType<typeof setTimeout> | undefined;

    const run = () => {
      const hash = window.location.hash;
      if (!hash || hash === "#") return;

      const deadline = Date.now() + 3000;

      const attempt = () => {
        if (cancelled) return;
        if (scrollToSection(hash)) {
          // Sections above this one grow as their images load, which drags the
          // target with them. One correction once the page is quiet is enough.
          settle = setTimeout(() => {
            if (!cancelled && window.location.hash === hash) scrollToSection(hash);
          }, 600);
          return;
        }
        if (Date.now() < deadline) frame = requestAnimationFrame(attempt);
      };

      frame = requestAnimationFrame(attempt);
    };

    run();
    window.addEventListener("hashchange", run);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (settle) clearTimeout(settle);
      window.removeEventListener("hashchange", run);
    };
  }, []);
}
